import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { getQuoteById, getQuoteItems, getQuotePaymentPlan, getQuoteNoteTerms, updateQuoteStatus, deleteQuote, duplicateQuote, approveAndConvertQuote } from '@/lib/quoteDb';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { quoteStatus } from '@/lib/statusDefs';

export default function QuoteDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [quote, setQuote] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [paymentPlan, setPaymentPlan] = useState<any[]>([]);
  const [noteTerms, setNoteTerms] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [conversionResult, setConversionResult] = useState<any>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  // Approval form
  const [approvalForm, setApprovalForm] = useState({
    useExistingCustomer: false,
    createNewCustomer: true,
    depositTaken: false,
    paymentMethod: 'Nakit',
    paymentDate: new Date().toISOString().split('T')[0],
    createReceipt: true,
    saleDate: new Date().toISOString().split('T')[0],
    createDelivery: true,
    createNotes: false,
    createInstallments: true,
  });

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const [qRes, iRes, pRes, ntRes] = await Promise.all([
      getQuoteById(id),
      getQuoteItems(id),
      getQuotePaymentPlan(id),
      getQuoteNoteTerms(id),
    ]);
    if (qRes.error) {
      setError(qRes.error);
    } else if (!qRes.data) {
      setError('Teklif bulunamadı');
    } else {
      setQuote(qRes.data);
      setItems(iRes.data || []);
      setPaymentPlan(pRes.data || []);
      setNoteTerms(ntRes.data || null);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const div = document.createElement('div');
    div.id = 'quote-print-root';
    div.style.display = 'none';
    document.body.appendChild(div);
    portalContainerRef.current = div;
    return () => {
      if (div && document.body.contains(div)) {
        document.body.removeChild(div);
      }
    };
  }, []);

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    await updateQuoteStatus(id, newStatus);
    setShowToast(`Teklif durumu "${newStatus}" olarak güncellendi`);
    setTimeout(() => setShowToast(null), 2000);
    loadData();
  };

  const handleDuplicate = async () => {
    if (!id) return;
    const { data: dup, error: err } = await duplicateQuote(id);
    if (err) {
      setShowToast('Kopya oluşturulamadı');
      setTimeout(() => setShowToast(null), 2000);
    } else if (dup) {
      navigate(`/quotes/${dup.id}`);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Bu teklifi silmek istediğinize emin misiniz?')) return;
    await deleteQuote(id);
    navigate('/quotes');
  };

  const isSenet = quote?.payment_type === 'Senet';
  const hasNoteTerms = noteTerms && (noteTerms.book_no || noteTerms.page_no) && noteTerms.due_date;
  const canApproveWithNotes = isSenet ? hasNoteTerms : true;

  const handleApprove = async () => {
    if (!id || approving) return;
    if (isSenet && !hasNoteTerms) {
      setShowToast('Senet bilgileri eksik: Vade tarihi, kitap no ve sayfa no zorunludur.');
      setTimeout(() => setShowToast(null), 4000);
      return;
    }
    setApproving(true);
    const { data, error: err } = await approveAndConvertQuote(id, approvalForm);
    if (err || (data && data.success === false)) {
      if (data?.already_converted) {
        setShowApprovalModal(false);
        setShowToast('Bu teklif daha önce satışa dönüştürülmüş.');
        setTimeout(() => { setShowToast(null); loadData(); }, 3000);
      } else {
        setShowToast('Onay hatası: ' + (data?.error || err || 'Bilinmeyen hata'));
        setTimeout(() => setShowToast(null), 4000);
      }
    } else {
      // Store conversion result
      setConversionResult(data);
      setShowApprovalModal(false);
      setShowToast('Teklif başarıyla satışa dönüştürüldü!');
      setTimeout(() => {
        setShowToast(null);
        loadData();
      }, 2500);
    }
    setApproving(false);
  };

  const handlePrint = () => {
    const printRoot = document.getElementById('quote-print-root');
    if (!printRoot) {
      setShowToast('Teklif belgesi hazırlanamadı. Lütfen tekrar deneyin.');
      setTimeout(() => setShowToast(null), 3000);
      return;
    }

    // Temporarily make visible for measurement (display:none elements have 0 height)
    const prevDisplay = printRoot.style.display;
    printRoot.style.display = 'block';
    printRoot.style.position = 'fixed';
    printRoot.style.visibility = 'hidden';
    printRoot.style.left = '0';
    printRoot.style.top = '0';
    printRoot.style.zIndex = '-1';

    const textContent = printRoot.textContent?.trim() || '';
    const hasContent = textContent.length > 10;
    const productCount = printRoot.querySelectorAll('tbody tr').length;
    const rootHeight = printRoot.offsetHeight;

    // Reset to original hidden state
    printRoot.style.display = prevDisplay;
    printRoot.style.position = '';
    printRoot.style.visibility = '';
    printRoot.style.left = '';
    printRoot.style.top = '';
    printRoot.style.zIndex = '';

    if (!hasContent || productCount < 1 || rootHeight < 50) {
      setShowToast('Teklif belgesi hazırlanamadı. Lütfen tekrar deneyin.');
      setTimeout(() => setShowToast(null), 3000);
      return;
    }

    // Ensure fonts are loaded before printing
    document.fonts.ready.then(() => {
      // Small delay to let browser complete layout
      setTimeout(() => {
        window.print();
      }, 100);
    });
  };

  const canApprove = quote && !['Onaylandı', 'Satışa Dönüştürüldü', 'İptal Edildi', 'Reddedildi'].includes(quote.status);
  const canEdit = quote && !['Onaylandı', 'Satışa Dönüştürüldü'].includes(quote.status);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
      <p className="text-sm text-foreground-500">Yükleniyor...</p>
    </div>
  );

  if (error || !quote) return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto text-center py-20">
      <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
      <p className="text-foreground-600 mb-4">{error || 'Teklif bulunamadı'}</p>
      <button onClick={() => navigate('/quotes')} className="btn-outline cursor-pointer">Tekliflere Dön</button>
    </div>
  );

  const subtotal = parseFloat(quote.subtotal) || 0;
  const totalAmount = parseFloat(quote.total_amount) || 0;

  return (
    <>
      {/* Hidden print portal - renders outside Layout overflow constraints */}
      {portalContainerRef.current && quote && createPortal(
        <div className="quote-print-doc" style={{ maxWidth: '190mm', width: '100%', margin: '0 auto', padding: '8mm', fontFamily: 'Inter, system-ui, sans-serif', background: 'white' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #e5e7eb', paddingBottom: '16px', marginBottom: '20px' }}>
            <div>
              <img src="https://storage.readdy-site.link/project_files/3da75122-8c1c-4e05-bb8b-0bbdfa25568f/c09a46c6-832e-47d9-869e-3bc0044c4baf_polatlar-crm-logo.png" alt="Polatlar Mobilya" style={{ height: '40px', objectFit: 'contain', marginBottom: '8px' }} />
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 2px 0' }}>Polatlar Mobilya</p>
              <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0' }}>Profesyonel Teklif Belgesi</p>
            </div>
            <div style={{ textAlign: 'right', fontSize: '12px' }}>
              <p style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 4px 0', color: '#111827' }}>{quote.quote_no}</p>
              <p style={{ margin: '0 0 2px 0', color: '#6b7280' }}>Revizyon: {quote.revision_no || 1}</p>
              <p style={{ margin: '0 0 2px 0', color: '#6b7280' }}>Teklif Tarihi: {quote.quote_date ? new Date(quote.quote_date).toLocaleDateString('tr-TR') : '-'}</p>
              <p style={{ margin: '0 0 2px 0', color: '#6b7280' }}>Geçerlilik: {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString('tr-TR') : '-'}</p>
              <p style={{ margin: '0', color: '#6b7280' }}>Hazırlayan: {quote.prepared_by || '-'}</p>
            </div>
          </div>

          {/* Prospect Info */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: '1px' }}>Müşteri Adayı Bilgileri</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '3px 8px', width: '25%', color: '#6b7280' }}>Ad Soyad:</td>
                  <td style={{ padding: '3px 8px', width: '25%', fontWeight: 600 }}>{quote.prospect_name || '-'}</td>
                  <td style={{ padding: '3px 8px', width: '25%', color: '#6b7280' }}>Telefon:</td>
                  <td style={{ padding: '3px 8px', width: '25%' }}>{quote.phone || '-'}</td>
                </tr>
                {quote.secondary_phone && (
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#6b7280' }}>2. Telefon:</td>
                    <td style={{ padding: '3px 8px' }}>{quote.secondary_phone}</td>
                    <td></td><td></td>
                  </tr>
                )}
                {quote.identity_no && (
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#6b7280' }}>TC Kimlik:</td>
                    <td style={{ padding: '3px 8px' }}>{quote.identity_no}</td>
                    <td style={{ padding: '3px 8px', color: '#6b7280' }}>İl / İlçe:</td>
                    <td style={{ padding: '3px 8px' }}>{[quote.city, quote.district].filter(Boolean).join(' / ') || '-'}</td>
                  </tr>
                )}
                {!quote.identity_no && (quote.city || quote.district) && (
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#6b7280' }}>İl / İlçe:</td>
                    <td style={{ padding: '3px 8px' }}>{[quote.city, quote.district].filter(Boolean).join(' / ') || '-'}</td>
                    <td></td><td></td>
                  </tr>
                )}
                {quote.address && (
                  <tr>
                    <td style={{ padding: '3px 8px', color: '#6b7280' }}>Adres:</td>
                    <td style={{ padding: '3px 8px', fontWeight: 500 }} colSpan={3}>{quote.address}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Products Table */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 10px 0', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: '1px' }}>Ürünler</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '6px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>#</th>
                  <th style={{ padding: '6px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Kategori</th>
                  <th style={{ padding: '6px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Cins</th>
                  <th style={{ padding: '6px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Marka</th>
                  <th style={{ padding: '6px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Ürün</th>
                  <th style={{ padding: '6px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Model/Renk/Ölçü</th>
                  <th style={{ padding: '6px', textAlign: 'center', fontWeight: 600, color: '#6b7280' }}>Adet</th>
                  <th style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Birim Fiyat</th>
                  <th style={{ padding: '6px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item: any, idx: number) => (
                  <tr key={item.id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '5px 6px', color: '#9ca3af' }}>{idx + 1}</td>
                    <td style={{ padding: '5px 6px', color: '#6b7280' }}>{item.category || '-'}</td>
                    <td style={{ padding: '5px 6px', color: '#6b7280' }}>{item.product_type || '-'}</td>
                    <td style={{ padding: '5px 6px', color: '#6b7280' }}>{item.brand || '-'}</td>
                    <td style={{ padding: '5px 6px', fontWeight: 600, color: '#111827' }}>{item.product_name}</td>
                    <td style={{ padding: '5px 6px', color: '#9ca3af', fontSize: '10px' }}>{[item.model_code, item.color, item.dimensions].filter(Boolean).join(' / ') || '-'}</td>
                    <td style={{ padding: '5px 6px', textAlign: 'center', color: '#374151' }}>{item.quantity}</td>
                    <td style={{ padding: '5px 6px', textAlign: 'right', color: '#374151' }}>{formatTRY(item.unit_price)}</td>
                    <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 600, color: '#111827' }}>{formatTRY((item.quantity || 1) * (parseFloat(item.unit_price) || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Price Summary */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <table style={{ width: '260px', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '5px 8px', color: '#6b7280' }}>Ara Toplam</td>
                  <td style={{ padding: '5px 8px', textAlign: 'right', color: '#374151' }}>{formatTRY(subtotal)}</td>
                </tr>
                {(parseFloat(quote.discount_amount) || 0) > 0 && (
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '5px 8px', color: '#ef4444' }}>İndirim</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#ef4444' }}>-{formatTRY(quote.discount_amount)}</td>
                  </tr>
                )}
                {(parseFloat(quote.shipping_amount) || 0) > 0 && (
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '5px 8px', color: '#6b7280' }}>Nakliye</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#374151' }}>{formatTRY(quote.shipping_amount)}</td>
                  </tr>
                )}
                {(parseFloat(quote.assembly_amount) || 0) > 0 && (
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '5px 8px', color: '#6b7280' }}>Montaj</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#374151' }}>{formatTRY(quote.assembly_amount)}</td>
                  </tr>
                )}
                <tr style={{ borderTop: '2px solid #1a1a1a' }}>
                  <td style={{ padding: '8px', fontWeight: 700, fontSize: '14px' }}>Genel Toplam</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, fontSize: '14px' }}>{formatTRY(totalAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Senet Planı (PDF / Hidden Print) */}
          {isSenet && noteTerms && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: '1px' }}>Senet Bilgileri</h3>
              <p style={{ fontSize: '12px', margin: '0 0 8px 0', color: '#6b7280' }}>
                Ödeme Tipi: <strong style={{ color: '#111827' }}>Senet</strong>
                {(parseFloat(quote.planned_deposit) || 0) > 0 && <span style={{ marginLeft: '16px' }}>Peşinat: <strong style={{ color: '#111827' }}>{formatTRY(quote.planned_deposit)}</strong></span>}
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Kitap No</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Sayfa No</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Sıra No</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Vade Tarihi</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Senet Tutarı</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{noteTerms.book_no || '-'}</td>
                    <td style={{ padding: '4px 8px' }}>{noteTerms.page_no || '-'}</td>
                    <td style={{ padding: '4px 8px' }}>{noteTerms.sequence_no || '-'}</td>
                    <td style={{ padding: '4px 8px' }}>{noteTerms.due_date ? new Date(noteTerms.due_date).toLocaleDateString('tr-TR') : '-'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{formatTRY(quote.planned_remaining || (totalAmount - (quote.planned_deposit || 0)))}</td>
                  </tr>
                </tbody>
              </table>
              {noteTerms.guarantor_name && (
                <p style={{ fontSize: '11px', margin: '8px 0 0 0', color: '#6b7280' }}>
                  Kefil: <strong style={{ color: '#374151' }}>{noteTerms.guarantor_name}</strong>
                  {noteTerms.guarantor_phone && <span style={{ marginLeft: '8px' }}>{noteTerms.guarantor_phone}</span>}
                </p>
              )}
              {!quote.converted_sale_id && (
                <p style={{ fontSize: '10px', margin: '8px 0 0 0', color: '#9ca3af', fontStyle: 'italic' }}>
                  * Senet bilgileri teklif onayında oluşturulacaktır.
                </p>
              )}
            </div>
          )}

          {/* Payment Plan */}
          {paymentPlan.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: '1px' }}>Ödeme Planı</h3>
              <p style={{ fontSize: '12px', margin: '0 0 8px 0', color: '#6b7280' }}>
                Ödeme Tipi: <strong style={{ color: '#111827' }}>{quote.payment_type || '-'}</strong>
                {(parseFloat(quote.planned_deposit) || 0) > 0 && <span style={{ marginLeft: '16px' }}>Peşinat: <strong style={{ color: '#111827' }}>{formatTRY(quote.planned_deposit)}</strong></span>}
                <span style={{ marginLeft: '16px' }}>Kalan: <strong style={{ color: '#111827' }}>{formatTRY(quote.planned_remaining)}</strong></span>
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>#</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Vade</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#6b7280' }}>Tutar</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, color: '#6b7280' }}>Yöntem</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentPlan.map((p: any, idx: number) => (
                    <tr key={p.id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '4px 8px', color: '#9ca3af' }}>{p.installment_no || idx + 1}</td>
                      <td style={{ padding: '4px 8px', color: '#374151' }}>{p.due_date ? new Date(p.due_date).toLocaleDateString('tr-TR') : '-'}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{formatTRY(p.amount)}</td>
                      <td style={{ padding: '4px 8px', color: '#6b7280' }}>{p.payment_method || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Delivery */}
          {quote.delivery_date && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '6px', borderBottom: '1px solid #e5e7eb', textTransform: 'uppercase', letterSpacing: '1px' }}>Teslimat</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px 8px', width: '25%', color: '#6b7280' }}>Tarih:</td>
                    <td style={{ padding: '3px 8px', fontWeight: 500 }}>{new Date(quote.delivery_date).toLocaleDateString('tr-TR')}</td>
                    {quote.delivery_time && <><td style={{ padding: '3px 8px', width: '25%', color: '#6b7280' }}>Saat:</td><td style={{ padding: '3px 8px' }}>{quote.delivery_time}</td></>}
                  </tr>
                  {quote.delivery_address && (
                    <tr>
                      <td style={{ padding: '3px 8px', color: '#6b7280' }}>Adres:</td>
                      <td style={{ padding: '3px 8px' }} colSpan={3}>{quote.delivery_address}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Notes */}
          {quote.public_note && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, margin: '0 0 8px 0', paddingBottom: '4px', borderBottom: '1px solid #e5e7eb' }}>Açıklama</h3>
              <p style={{ fontSize: '12px', margin: '0', padding: '8px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '4px' }}>{quote.public_note}</p>
            </div>
          )}

          {/* Signature */}
          <div style={{ marginTop: '30px', display: 'flex', gap: '60px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '60px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Müşteri İmza</span>
              </div>
              <p style={{ fontSize: '11px', margin: '6px 0 0 0', color: '#6b7280' }}>Tarih: .... / .... / 20....</p>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ height: '60px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '4px' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af' }}>Yetkili İmza</span>
              </div>
              <p style={{ fontSize: '11px', margin: '6px 0 0 0', color: '#6b7280' }}>Tarih: .... / .... / 20....</p>
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '20px', paddingTop: '10px', borderTop: '1px solid #e5e7eb', textAlign: 'center' }}>
            <p style={{ fontSize: '10px', color: '#9ca3af', margin: '0 0 2px 0' }}>Bu teklif Polatlar Mobilya tarafından {quote.quote_date ? new Date(quote.quote_date).toLocaleDateString('tr-TR') : '-'} tarihinde hazırlanmıştır.</p>
            <p style={{ fontSize: '10px', color: '#d1d5db', margin: '0' }}>Geçerlilik: {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString('tr-TR') : '-'} · {quote.quote_no} · Rev: {quote.revision_no || 1}</p>
          </div>
        </div>,
        portalContainerRef.current
      )}

      {showToast && (
        <div className={`fixed top-4 right-4 z-[100] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in ${showToast.includes('başarıyla') || showToast.includes('dönüştürüldü') ? 'bg-emerald-500' : 'bg-amber-500'} text-white no-print`}>
          <i className={showToast.includes('başarıyla') || showToast.includes('dönüştürüldü') ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          <span className="text-sm font-medium">{showToast}</span>
        </div>
      )}

      <div className="p-4 md:p-6 max-w-[1000px] mx-auto">
        {/* Action Bar */}
        <div className="flex flex-wrap items-center gap-2 mb-6 no-print">
          <button onClick={() => navigate('/quotes')} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
            <i className="ri-arrow-left-line text-foreground-600"></i>
          </button>
          <h1 className="text-xl font-bold text-foreground-900 font-heading mr-auto">{quote.quote_no}</h1>
          <Badge badgeClass={quoteStatus.badgeClass(quote.status)} icon={quoteStatus.icon(quote.status)}>{quote.status}</Badge>

          <button onClick={handlePrint} className="px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-printer-line"></i> Yazdır
          </button>
          {canEdit && (
            <button onClick={() => navigate(`/quotes/${id}/edit`)} className="px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-edit-line"></i> Düzenle
            </button>
          )}
          <button onClick={handleDuplicate} className="px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200 text-foreground-700 text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
            <i className="ri-file-copy-line"></i> Kopyala
          </button>

          {canApprove && (
            <>
              <button onClick={() => setShowApprovalModal(true)} className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-check-double-line"></i> Onayla
              </button>
              <button onClick={() => handleStatusChange('Reddedildi')} className="px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                <i className="ri-close-circle-line"></i> Reddet
              </button>
            </>
          )}

          {!['Onaylandı', 'Satışa Dönüştürüldü', 'İptal Edildi'].includes(quote.status) && (
            <button onClick={handleDelete} className="px-3 py-2 rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 text-sm cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
        </div>

        {/* Status Quick Actions */}
        {canEdit && (
          <div className="flex flex-wrap gap-1.5 mb-6 no-print">
            {['Taslak', 'Müşteriye Sunuldu', 'Revize Bekliyor', 'Beklemede'].map(s => (
              <button key={s} onClick={() => handleStatusChange(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${quote.status === s ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* PRINTABLE AREA */}
        <div className="print-area bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8 space-y-8">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-background-200 pb-5">
            <div>
              <img
                src="https://storage.readdy-site.link/project_files/3da75122-8c1c-4e05-bb8b-0bbdfa25568f/c09a46c6-832e-47d9-869e-3bc0044c4baf_polatlar-crm-logo.png"
                alt="Polatlar Mobilya"
                className="h-10 object-contain mb-3"
              />
              <p className="text-sm text-foreground-600">Polatlar Mobilya</p>
              <p className="text-xs text-foreground-500">Profesyonel Teklif Belgesi</p>
            </div>
            <div className="text-right">
              <h2 className="text-lg font-bold text-foreground-900">{quote.quote_no}</h2>
              <p className="text-xs text-foreground-500 mt-1">Revizyon: {quote.revision_no || 1}</p>
              <p className="text-xs text-foreground-500">Teklif Tarihi: {quote.quote_date ? new Date(quote.quote_date).toLocaleDateString('tr-TR') : '-'}</p>
              <p className="text-xs text-foreground-500">Geçerlilik: {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString('tr-TR') : '-'}</p>
              <p className="text-xs text-foreground-500">Hazırlayan: {quote.prepared_by || '-'}</p>
            </div>
          </div>

          {/* Prospect Info */}
          <div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-3">Müşteri Adayı Bilgileri</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div><span className="text-foreground-500">Ad Soyad:</span> <span className="text-foreground-800 font-medium">{quote.prospect_name || '-'}</span></div>
              <div><span className="text-foreground-500">Telefon:</span> <span className="text-foreground-800">{quote.phone || '-'}</span></div>
              {quote.secondary_phone && <div><span className="text-foreground-500">İkinci Telefon:</span> <span className="text-foreground-800">{quote.secondary_phone}</span></div>}
              {quote.identity_no && <div><span className="text-foreground-500">TC Kimlik:</span> <span className="text-foreground-800">{quote.identity_no}</span></div>}
              {quote.city && <div><span className="text-foreground-500">İl:</span> <span className="text-foreground-800">{quote.city}</span></div>}
              {quote.district && <div><span className="text-foreground-500">İlçe:</span> <span className="text-foreground-800">{quote.district}</span></div>}
              {quote.address && <div className="sm:col-span-2"><span className="text-foreground-500">Adres:</span> <span className="text-foreground-800">{quote.address}</span></div>}
            </div>
          </div>

          {/* Products Table */}
          <div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-3">Ürünler</h4>
            <div className="overflow-x-auto border border-background-200 rounded-lg">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-foreground-500">#</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-foreground-500">Kategori</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-foreground-500">Cins</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-foreground-500">Marka</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-foreground-500">Ürün</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-foreground-500">Model/Renk/Ölçü</th>
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-foreground-500">Adet</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-foreground-500">Birim Fiyat</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-foreground-500">Toplam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {items.map((item: any, idx: number) => (
                    <tr key={item.id || idx}>
                      <td className="px-3 py-2.5 text-foreground-500">{idx + 1}</td>
                      <td className="px-3 py-2.5 text-foreground-700">{item.category || '-'}</td>
                      <td className="px-3 py-2.5 text-foreground-700">{item.product_type || '-'}</td>
                      <td className="px-3 py-2.5 text-foreground-700">{item.brand || '-'}</td>
                      <td className="px-3 py-2.5 text-foreground-800 font-medium">{item.product_name}</td>
                      <td className="px-3 py-2.5 text-foreground-500 text-xs">
                        {[item.model_code, item.color, item.dimensions].filter(Boolean).join(' / ') || '-'}
                      </td>
                      <td className="px-3 py-2.5 text-center text-foreground-700">{item.quantity}</td>
                      <td className="px-3 py-2.5 text-right text-foreground-700">{formatTRY(item.unit_price)}</td>
                      <td className="px-3 py-2.5 text-right text-foreground-800 font-medium">{formatTRY((item.quantity || 1) * (parseFloat(item.unit_price) || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Price Summary */}
          <div className="flex justify-end">
            <div className="w-full max-w-xs space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-foreground-500">Ara Toplam</span><span className="text-foreground-700">{formatTRY(subtotal)}</span></div>
              {(parseFloat(quote.discount_amount) || 0) > 0 && (
                <div className="flex justify-between"><span className="text-foreground-500">İndirim</span><span className="text-red-600">-{formatTRY(quote.discount_amount)}</span></div>
              )}
              {(parseFloat(quote.shipping_amount) || 0) > 0 && (
                <div className="flex justify-between"><span className="text-foreground-500">Nakliye</span><span className="text-foreground-700">{formatTRY(quote.shipping_amount)}</span></div>
              )}
              {(parseFloat(quote.assembly_amount) || 0) > 0 && (
                <div className="flex justify-between"><span className="text-foreground-500">Montaj</span><span className="text-foreground-700">{formatTRY(quote.assembly_amount)}</span></div>
              )}
              {(parseFloat(quote.tax_amount) || 0) > 0 && (
                <div className="flex justify-between"><span className="text-foreground-500">KDV</span><span className="text-foreground-700">{formatTRY(quote.tax_amount)}</span></div>
              )}
              <div className="flex justify-between border-t border-background-200 pt-2 font-bold text-base">
                <span>Genel Toplam</span>
                <span className="text-foreground-900">{formatTRY(totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Senet Bilgileri (Görünür) */}
          {isSenet && noteTerms && (
            <div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">Senet Bilgileri</h4>
              <div className="text-sm mb-2">
                <span className="text-foreground-500">Ödeme Tipi:</span> <span className="text-foreground-800 font-medium">Senet</span>
                {(parseFloat(quote.planned_deposit) || 0) > 0 && (
                  <span className="ml-4"><span className="text-foreground-500">Peşinat:</span> <span className="text-foreground-800 font-medium">{formatTRY(quote.planned_deposit)}</span></span>
                )}
                <span className="ml-4"><span className="text-foreground-500">Senet Tutarı:</span> <span className="text-foreground-800 font-medium">{formatTRY(quote.planned_remaining || (totalAmount - (quote.planned_deposit || 0)))}</span></span>
              </div>
              <div className="overflow-x-auto border border-background-200 rounded-lg">
                <table className="w-full text-sm min-w-[500px]">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">Kitap No</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">Sayfa No</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">Sıra No</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">Vade Tarihi</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-foreground-500">Tutar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    <tr>
                      <td className="px-3 py-2 text-foreground-800 font-medium">{noteTerms.book_no || '-'}</td>
                      <td className="px-3 py-2 text-foreground-700">{noteTerms.page_no || '-'}</td>
                      <td className="px-3 py-2 text-foreground-700">{noteTerms.sequence_no || '-'}</td>
                      <td className="px-3 py-2 text-foreground-700">{noteTerms.due_date ? new Date(noteTerms.due_date).toLocaleDateString('tr-TR') : '-'}</td>
                      <td className="px-3 py-2 text-right text-foreground-800 font-medium">{formatTRY(quote.planned_remaining || (totalAmount - (quote.planned_deposit || 0)))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {noteTerms.guarantor_name && (
                <p className="text-sm text-foreground-600 mt-2">
                  Kefil: <span className="font-medium text-foreground-800">{noteTerms.guarantor_name}</span>
                  {noteTerms.guarantor_phone && <span className="ml-3">{noteTerms.guarantor_phone}</span>}
                </p>
              )}
              {!quote.converted_sale_id && (
                <p className="text-xs text-foreground-400 mt-2 italic">* Senet bilgileri teklif onayında oluşturulacaktır.</p>
              )}
            </div>
          )}

          {/* Planlanan Ödeme Planı (Görünür) */}
          {paymentPlan.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">Planlanan Ödeme Planı</h4>
              <div className="text-sm mb-2">
                <span className="text-foreground-500">Ödeme Tipi:</span> <span className="text-foreground-800 font-medium">{quote.payment_type || '-'}</span>
                {(parseFloat(quote.planned_deposit) || 0) > 0 && (
                  <span className="ml-4"><span className="text-foreground-500">Peşinat:</span> <span className="text-foreground-800 font-medium">{formatTRY(quote.planned_deposit)}</span></span>
                )}
                <span className="ml-4"><span className="text-foreground-500">Kalan:</span> <span className="text-foreground-800 font-medium">{formatTRY(quote.planned_remaining)}</span></span>
              </div>
              <div className="overflow-x-auto border border-background-200 rounded-lg">
                <table className="w-full text-sm min-w-[400px]">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">Vade</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-foreground-500">Tutar</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-foreground-500">Yöntem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {paymentPlan.map((p: any, idx: number) => (
                      <tr key={p.id || idx}>
                        <td className="px-3 py-2 text-foreground-500">{p.installment_no || idx + 1}</td>
                        <td className="px-3 py-2 text-foreground-700">{p.due_date ? new Date(p.due_date).toLocaleDateString('tr-TR') : '-'}</td>
                        <td className="px-3 py-2 text-right text-foreground-800 font-medium">{formatTRY(p.amount)}</td>
                        <td className="px-3 py-2 text-foreground-600">{p.payment_method || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Delivery */}
          {quote.delivery_date && (
            <div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">Teslimat Bilgileri</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-foreground-500">Tarih:</span> <span className="text-foreground-800">{new Date(quote.delivery_date).toLocaleDateString('tr-TR')}</span></div>
                {quote.delivery_time && <div><span className="text-foreground-500">Saat:</span> <span className="text-foreground-800">{quote.delivery_time}</span></div>}
                {quote.delivery_address && <div className="sm:col-span-2"><span className="text-foreground-500">Adres:</span> <span className="text-foreground-800">{quote.delivery_address}</span></div>}
                {(parseFloat(quote.assembly_amount) || 0) > 0 && <div><span className="text-foreground-500">Montaj:</span> <span className="text-foreground-800 text-accent-600">Gerekli</span></div>}
              </div>
            </div>
          )}

          {/* Notes */}
          {quote.public_note && (
            <div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-2">Açıklama</h4>
              <p className="text-sm text-foreground-600 bg-background-100 rounded-lg p-3">{quote.public_note}</p>
            </div>
          )}

          {/* Signature Area */}
          <div className="border-t border-background-200 pt-6 mt-6">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <p className="text-xs text-foreground-500 mb-8">Müşteri İmza:</p>
                <div className="border-t border-foreground-300 pt-2">
                  <p className="text-xs text-foreground-500">Tarih: .... / .... / 20....</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-8">Yetkili İmza:</p>
                <div className="border-t border-foreground-300 pt-2">
                  <p className="text-xs text-foreground-500">Tarih: .... / .... / 20....</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-[10px] text-foreground-400 border-t border-background-200 pt-4">
            <p>Bu teklif Polatlar Mobilya tarafından {quote.quote_date ? new Date(quote.quote_date).toLocaleDateString('tr-TR') : '-'} tarihinde hazırlanmıştır.</p>
            <p>Geçerlilik: {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString('tr-TR') : '-'} · Teklif No: {quote.quote_no} · Revizyon: {quote.revision_no || 1}</p>
          </div>
        </div>

        {/* Converted Sale + Note Link */}
        {quote.converted_sale_id && (
          <div className="mt-4 space-y-3 no-print">
            <div className="p-4 bg-primary-50 border border-primary-200 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                  <i className="ri-check-line text-background-50"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-primary-800">Satışa Dönüştürüldü</p>
                  <p className="text-xs text-primary-600">Satış No: {quote.converted_sale_id?.substring(0, 8)}...</p>
                </div>
              </div>
              <button onClick={() => navigate(`/sales/${quote.converted_sale_id}`)} className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap">
                Satışı Görüntüle
              </button>
            </div>

            {/* Show linked promissory note if exists */}
            {conversionResult?.note_id && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center">
                    <i className="ri-bill-line text-background-50"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Senet Oluşturuldu</p>
                    <p className="text-xs text-amber-600">Senet No: {conversionResult.note_no}</p>
                  </div>
                </div>
                <button onClick={() => navigate(`/promissory-notes`)} className="px-4 py-2 rounded-lg bg-amber-500 text-background-50 text-sm cursor-pointer whitespace-nowrap">
                  Senetleri Gör
                </button>
              </div>
            )}

            {conversionResult?.customer_id && (
              <div className="p-4 bg-secondary-50 border border-secondary-200 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center">
                    <i className="ri-user-3-line text-background-50"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-secondary-800">Müşteri {conversionResult.customer_no ? 'Oluşturuldu' : 'Eşleştirildi'}</p>
                    <p className="text-xs text-secondary-600">Müşteri No: {conversionResult.customer_no || '-'}</p>
                  </div>
                </div>
                <button onClick={() => navigate(`/customers/${conversionResult.customer_id}`)} className="px-4 py-2 rounded-lg bg-secondary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap">
                  Müşteriye Git
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* APPROVAL MODAL */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4 no-print" onClick={() => setShowApprovalModal(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-foreground-900">Teklif Onayı</h3>
              <button onClick={() => setShowApprovalModal(false)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="space-y-5">
              {/* Summary */}
              <div className="bg-background-100 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-foreground-500">Müşteri</span><span className="font-medium">{quote.prospect_name}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Teklif No</span><span className="font-medium">{quote.quote_no}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Ürün Sayısı</span><span className="font-medium">{items.length}</span></div>
                <div className="flex justify-between font-bold text-base border-t border-background-200 pt-2"><span>Genel Toplam</span><span>{formatTRY(totalAmount)}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Ödeme Şekli</span><span className="font-medium">{quote.payment_type}</span></div>
                <div className="flex justify-between"><span className="text-foreground-500">Planlanan Peşinat</span><span className="font-medium">{formatTRY(quote.planned_deposit)}</span></div>
                {paymentPlan.length > 0 && <div className="flex justify-between"><span className="text-foreground-500">Taksit Sayısı</span><span className="font-medium">{paymentPlan.length}</span></div>}
                {quote.delivery_date && <div className="flex justify-between"><span className="text-foreground-500">Teslimat</span><span className="font-medium">{new Date(quote.delivery_date).toLocaleDateString('tr-TR')}</span></div>}
              </div>

              {/* Options */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-foreground-900">Onay Seçenekleri</h4>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="depositTaken" checked={approvalForm.depositTaken}
                    onChange={e => setApprovalForm({ ...approvalForm, depositTaken: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 cursor-pointer" />
                  <label htmlFor="depositTaken" className="text-sm text-foreground-600 cursor-pointer">Peşinat şimdi alındı ({formatTRY(quote.planned_deposit || 0)})</label>
                </div>

                {approvalForm.depositTaken && (
                  <div className="grid grid-cols-2 gap-3 ml-6">
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1">Ödeme Yöntemi</label>
                      <select value={approvalForm.paymentMethod} onChange={e => setApprovalForm({ ...approvalForm, paymentMethod: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                        {['Nakit', 'Kredi Kartı', 'Havale / EFT'].map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1">Ödeme Tarihi</label>
                      <input type="date" value={approvalForm.paymentDate}
                        onChange={e => setApprovalForm({ ...approvalForm, paymentDate: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="createDelivery" checked={approvalForm.createDelivery}
                    onChange={e => setApprovalForm({ ...approvalForm, createDelivery: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 cursor-pointer" />
                  <label htmlFor="createDelivery" className="text-sm text-foreground-600 cursor-pointer">Teslimat kaydı oluştur</label>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="createInstallments" checked={approvalForm.createInstallments}
                    onChange={e => setApprovalForm({ ...approvalForm, createInstallments: e.target.checked })}
                    className="w-4 h-4 rounded border-background-300 cursor-pointer" />
                  <label htmlFor="createInstallments" className="text-sm text-foreground-600 cursor-pointer">Taksit planı oluştur</label>
                </div>

                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1">Satış Tarihi</label>
                  <input type="date" value={approvalForm.saleDate}
                    onChange={e => setApprovalForm({ ...approvalForm, saleDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
                </div>
              </div>

              {/* Senet Bilgileri Özeti */}
              {isSenet && noteTerms && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1 text-sm">
                  <h4 className="text-xs font-semibold text-amber-800 mb-1">Senet Bilgileri (Onayda oluşturulacak)</h4>
                  <div className="flex justify-between"><span className="text-amber-700">Kitap No:</span><span className="font-medium text-amber-900">{noteTerms.book_no || '-'}</span></div>
                  <div className="flex justify-between"><span className="text-amber-700">Sayfa No:</span><span className="font-medium text-amber-900">{noteTerms.page_no || '-'}</span></div>
                  {noteTerms.sequence_no && <div className="flex justify-between"><span className="text-amber-700">Sıra No:</span><span className="font-medium text-amber-900">{noteTerms.sequence_no}</span></div>}
                  <div className="flex justify-between"><span className="text-amber-700">Vade:</span><span className="font-medium text-amber-900">{noteTerms.due_date ? new Date(noteTerms.due_date).toLocaleDateString('tr-TR') : '-'}</span></div>
                  <div className="flex justify-between"><span className="text-amber-700">Senet Tutarı:</span><span className="font-bold text-amber-900">{formatTRY(quote.planned_remaining || (totalAmount - (quote.planned_deposit || 0)))}</span></div>
                  {noteTerms.guarantor_name && <div className="flex justify-between"><span className="text-amber-700">Kefil:</span><span className="font-medium text-amber-900">{noteTerms.guarantor_name}</span></div>}
                </div>
              )}

              {/* Senet Bilgileri Eksik Uyarısı */}
              {isSenet && !hasNoteTerms && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                  <div>
                    <p className="text-xs font-medium text-red-800">Senet bilgileri eksik</p>
                    <p className="text-xs text-red-600 mt-0.5">Vade tarihi, kitap no ve sayfa no zorunludur. Teklif düzenleme ekranından senet bilgilerini girin.</p>
                  </div>
                </div>
              )}

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-error-warning-line text-amber-500 mt-0.5"></i>
                <div>
                  <p className="text-xs font-medium text-amber-800">Bu işlem geri alınamaz</p>
                  <p className="text-xs text-amber-600 mt-0.5">Onaylandığında müşteri kaydı, satış, ödeme ve teslimat kayıtları otomatik oluşturulur.</p>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowApprovalModal(false)} className="btn-outline cursor-pointer whitespace-nowrap">İptal</button>
                <button onClick={handleApprove} disabled={approving || !canApproveWithNotes} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                  {approving ? <><i className="ri-loader-4-line animate-spin"></i>Onaylanıyor...</> : <><i className="ri-check-double-line"></i>Onayla ve Satışa Dönüştür</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}